<%
    const currentPlan = currentPackage || 'FREE';
    const premium = premiumData || {};
    const hasSubscription = !!premium.lsSubscriptionId;
    const subStatus = premium.status || null;
    const isCancelled = subStatus === 'cancelled';
    const isActive = subStatus === 'active';
    const isPastDue = subStatus === 'past_due';

    const packages = [
        {
            id: 'FREE',
            name: 'Free',
            monthlyPrice: 0,
            yearlyPrice: 0,
            icon: 'solar:shield-line-duotone',
            iconColor: 'secondary',
            features: [
                { label: '25 generations / month', included: true },
                { label: '1 User', included: true },
                { label: 'Hard generation limit', included: true, tooltip: 'Hard limit: Once you reach your generation limit, you cannot process any more generations until the next billing cycle.' },
                { label: 'Packs', included: false, tooltip: 'Packs let you bundle multiple document templates together and process them in a single request — saving time and simplifying bulk workflows. Available on Business and Enterprise plans.' },
                { label: 'Role management', included: false },
                { label: 'Soft limit overage', included: false, tooltip: 'Soft limit: Instead of being blocked when you hit your monthly limit, you get a temporary 10% bonus allowance to keep processing generations.' }
            ]
        },
        {
            id: 'STARTER',
            name: 'Starter',
            monthlyPrice: 29,
            yearlyPrice: 23,
            savePct: 21,
            icon: 'solar:rocket-line-duotone',
            iconColor: 'primary',
            features: [
                { label: '1,000 generations / month', included: true },
                { label: '1 User', included: true },
                { label: 'Hard generation limit', included: true, tooltip: 'Hard limit: Once you reach your generation limit, you cannot process any more generations until the next billing cycle.' },
                { label: 'Packs', included: false, tooltip: 'Packs let you bundle multiple document templates together and process them in a single request — saving time and simplifying bulk workflows. Available on Business and Enterprise plans.' },
                { label: 'Role management', included: false },
                { label: 'Soft limit overage', included: false, tooltip: 'Soft limit: Instead of being blocked when you hit your monthly limit, you get a temporary 10% bonus allowance to keep processing generation.' }
            ]
        },
        {
            id: 'BUSINESS',
            name: 'Business',
            monthlyPrice: 49,
            yearlyPrice: 39,
            savePct: 20,
            icon: 'solar:case-line-duotone',
            iconColor: 'success',
            popular: true,
            features: [
                { label: '10,000 generations / month', included: true },
                { label: '5 Users', included: true },
                { label: 'Soft limit overage', included: true, tooltip: 'Soft limit: Instead of being blocked when you hit your monthly limit, you get a temporary 10% bonus allowance to keep processing generations.' },
                { label: 'Packs', included: true, tooltip: 'Packs let you bundle multiple document templates together and process them in a single request — saving time and simplifying bulk workflows.' },
                { label: 'Role management', included: true },
                { label: 'Priority support', included: true }
            ]
        },
        {
            id: 'ENTERPRISE',
            name: 'Enterprise',
            monthlyPrice: 99,
            yearlyPrice: 79,
            savePct: 20,
            icon: 'solar:buildings-3-line-duotone',
            iconColor: 'warning',
            features: [
                { label: '50,000 generations / month', included: true },
                { label: '25 Users', included: true },
                { label: 'Soft limit overage', included: true, tooltip: 'Soft limit: Instead of being blocked when you hit your monthly limit, you get a temporary 10% bonus allowance to keep processing generations.' },
                { label: 'Packs', included: true, tooltip: 'Packs let you bundle multiple document templates together and process them in a single request — saving time and simplifying bulk workflows.' },
                { label: 'Role management', included: true },
                { label: 'Dedicated support', included: true }
            ]
        }
    ].filter( pkg => currentPlan === 'FREE' || pkg.id !== 'FREE' );

    const statusBadgeClass = {
        active: 'bg-success',
        cancelled: 'bg-warning',
        expired: 'bg-danger',
        past_due: 'bg-danger',
        paused: 'bg-secondary'
    };
    const statusLabel = {
        active: 'Active',
        cancelled: 'Cancelled',
        expired: 'Expired',
        past_due: 'Past Due',
        paused: 'Paused'
    };
%>

<!-- Current Plan Banner -->
<div class="card mb-4">
    <div class="card-body">
        <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-3">
                <div class="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center" style="width: 48px; height: 48px;">
                    <iconify-icon icon="solar:crown-line-duotone" class="fs-7 text-primary"></iconify-icon>
                </div>
                <div>
                    <h5 class="mb-1">Your Current Plan</h5>
                    <div class="d-flex align-items-center gap-2">
                        <span class="badge bg-primary fs-2 fw-bolder text-uppercase"><%= currentPlan %></span>
                        <% if (subStatus) { %>
                        <span class="badge <%= statusBadgeClass[subStatus] || 'bg-secondary' %> fs-1 fw-bolder"><%= statusLabel[subStatus] || subStatus %></span>
                        <% } %>
                        <% if (premium.billingCycle) { %>
                        <span class="badge bg-light text-dark fs-1 fw-bolder text-capitalize"><%= premium.billingCycle %></span>
                        <% } %>
                    </div>
                    <% if (isCancelled && premium.endsAt) { %>
                    <p class="text-warning fs-2 mb-0 mt-1">Access until <%= new Date(premium.endsAt).toLocaleDateString() %></p>
                    <% } %>
                    <% if (isPastDue) { %>
                    <p class="text-danger fs-2 mb-0 mt-1">Payment failed. Please update your payment method.</p>
                    <% } %>
                </div>
            </div>
            <div class="d-flex gap-2">
                <% if (hasSubscription && premium.customerPortalUrl) { %>
                <a href="#" class="btn btn-outline-primary btn-sm" data-type="manage-billing">
                    <iconify-icon icon="solar:wallet-line-duotone" class="me-1"></iconify-icon>
                    Manage Billing
                </a>
                <% } %>
                <% if (hasSubscription && isActive) { %>
                <button class="btn btn-outline-danger btn-sm" data-type="cancel-subscription">
                    Cancel Subscription
                </button>
                <% } %>
            </div>
        </div>
    </div>
</div>

<!-- Pricing Header -->
<div class="text-center mb-4">
    <h4 class="fw-bolder mb-2">Choose the plan that fits your needs</h4>
    <p class="text-muted fs-3 mb-0">Scale as you grow with flexible premium packages</p>
</div>

<!-- Billing Toggle -->
<div class="d-flex align-items-center justify-content-center mb-4 gap-3">
    <span class="fw-bolder fs-3 text-muted" data-type="billing-label-monthly">Monthly</span>
    <div class="form-check form-switch mb-0">
        <input class="form-check-input" type="checkbox" role="switch" data-type="billing-toggle" style="cursor: pointer;" checked>
    </div>
    <span class="fw-bolder fs-3" data-type="billing-label-yearly">Yearly</span>
    <span class="badge bg-success-subtle text-success fw-bolder fs-2 ms-1">Save up to 21%</span>
</div>

<!-- Pricing Cards -->
<div class="row justify-content-center">
    <% packages.forEach(function(pkg) { %>
    <div class="col-sm-6 <%= packages.length === 4 ? 'col-xl-3' : 'col-xl-4' %>">
        <div class="card <%= currentPlan === pkg.id ? 'border border-primary' : '' %>" style="min-height: 480px;">
            <div class="card-body d-flex flex-column">
                <% if (pkg.popular) { %>
                <div class="text-end mb-2">
                    <span class="badge fw-bolder py-1 bg-warning-subtle text-warning text-uppercase fs-2 rounded-3">Popular</span>
                </div>
                <% } else { %>
                <div class="mb-2" style="height: 22px;"></div>
                <% } %>

                <h5 class="fw-bolder text-uppercase text-center mb-3"><%= pkg.name %></h5>

                <div class="my-3 d-flex align-items-center justify-content-center" style="height: 64px;">
                    <div class="rounded-circle bg-<%= pkg.iconColor %>-subtle d-flex align-items-center justify-content-center" style="width: 64px; height: 64px;">
                        <iconify-icon icon="<%= pkg.icon %>" class="fs-9 text-<%= pkg.iconColor %>"></iconify-icon>
                    </div>
                </div>

                <% if (pkg.monthlyPrice === 0) { %>
                <div class="mb-3 text-center" style="height: 85px; display: flex; flex-direction: column; align-items: center; justify-content: center;">
                    <h2 class="fw-bolder fs-8 mb-0">Free</h2>
                    <span class="badge bg-secondary-subtle text-secondary fw-bolder fs-1 mt-2">Free forever</span>
                </div>
                <% } else { %>
                <div class="mb-3 text-center" style="height: 85px; display: flex; flex-direction: column; align-items: center; justify-content: center;">
                    <div data-type="price-monthly" style="display: none;">
                        <div class="d-flex align-items-end justify-content-center">
                            <span class="fw-bolder fs-6">$</span>
                            <h2 class="fw-bolder fs-10 mb-0 mx-1"><%= pkg.monthlyPrice %></h2>
                            <span class="fs-3 text-muted mb-1">/mo</span>
                        </div>
                    </div>
                    <div data-type="price-yearly">
                        <div class="d-flex align-items-end justify-content-center">
                            <span class="fw-bolder fs-6">$</span>
                            <h2 class="fw-bolder fs-10 mb-0 mx-1"><%= pkg.yearlyPrice %></h2>
                            <span class="fs-3 text-muted mb-1">/mo</span>
                        </div>
                        <span class="badge bg-success-subtle text-success fw-bolder fs-1 mt-1">Save <%= pkg.savePct %>%</span>
                        <div class="text-muted fs-1 mt-1">when billed yearly</div>
                    </div>
                </div>
                <% } %>

                <ul class="list-unstyled mb-4 flex-grow-1">
                    <% pkg.features.forEach(function(feature) { %>
                    <li class="d-flex align-items-center gap-2 py-2">
                        <% if (feature.included) { %>
                            <iconify-icon icon="solar:check-circle-bold" class="fs-5 text-success flex-shrink-0"></iconify-icon>
                            <span><%= feature.label %></span>
                        <% } else { %>
                            <iconify-icon icon="solar:close-circle-bold" class="fs-5 text-muted opacity-50 flex-shrink-0"></iconify-icon>
                            <span class="text-muted"><%= feature.label %></span>
                        <% } %>
                        <% if (feature.tooltip) { %>
                            <span class="premium-tooltip-wrap" style="position: relative; display: inline-flex; cursor: help;">
                                <iconify-icon icon="solar:question-circle-line-duotone" class="fs-5 text-muted flex-shrink-0"></iconify-icon>
                                <span class="premium-tooltip-text"><%= feature.tooltip %></span>
                            </span>
                        <% } %>
                    </li>
                    <% }); %>
                </ul>

                <% if (currentPlan === pkg.id) { %>
                <button class="btn btn-outline-primary fw-bolder py-6 w-100" disabled>
                    Current Plan
                </button>
                <% } else if (pkg.id === 'FREE') { %>
                    <% /* Don't show a button for Free when user has an active subscription */ %>
                    <% if (!hasSubscription || isCancelled) { %>
                    <button class="btn btn-outline-secondary fw-bolder py-6 w-100" disabled>
                        Free Plan
                    </button>
                    <% } else { %>
                    <button class="btn btn-outline-secondary fw-bolder py-6 w-100" disabled>
                        Cancel to Downgrade
                    </button>
                    <% } %>
                <% } else { %>
                <%
                    const paidPlanOrder = { STARTER: 1, BUSINESS: 2, ENTERPRISE: 3 };
                    const currentOrder = paidPlanOrder[currentPlan] || 0;
                    const pkgOrder = paidPlanOrder[pkg.id] || 0;
                    let btnLabel = 'Choose ' + pkg.name;
                    if (hasSubscription && isActive) {
                        btnLabel = pkgOrder > currentOrder ? 'Upgrade' : 'Downgrade';
                    }
                %>
                <button class="btn btn-primary fw-bolder py-6 w-100"
                        data-type="select-plan"
                        data-plan-id="<%= pkg.id %>"
                        data-plan-name="<%= pkg.name %>"
                        data-plan-monthly="<%= pkg.monthlyPrice %>"
                        data-plan-yearly="<%= pkg.yearlyPrice %>">
                    <%= btnLabel %>
                </button>
                <% } %>
            </div>
        </div>
    </div>
    <% }); %>
</div>

<!-- Confirm Plan Modal -->
<div class="modal fade" id="confirmPlanModal" tabindex="-1" aria-labelledby="confirmPlanModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title" id="confirmPlanModalLabel">Confirm Subscription</h5>
                <button type="button" class="btn-close" data-type="plan-modal-close" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                <div class="text-center py-3">
                    <div class="rounded-circle bg-primary-subtle d-flex align-items-center justify-content-center mx-auto mb-3" style="width: 64px; height: 64px;">
                        <iconify-icon icon="solar:crown-line-duotone" class="fs-9 text-primary"></iconify-icon>
                    </div>
                    <h5 class="mb-2"><span data-type="confirm-action-label">Subscribe to</span> <span data-type="confirm-plan-name"></span>?</h5>
                    <p class="text-muted mb-2">You are about to change your subscription to the <strong data-type="confirm-plan-name-bold"></strong> plan.</p>
                    <p class="fw-bolder fs-4 mb-0" data-type="confirm-plan-price"></p>
                </div>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-type="plan-modal-cancel">Cancel</button>
                <button type="button" class="btn btn-primary" data-type="plan-modal-confirm">Confirm</button>
            </div>
        </div>
    </div>
</div>

<!-- Cancel Subscription Modal -->
<div class="modal fade" id="cancelSubscriptionModal" tabindex="-1" aria-labelledby="cancelSubscriptionModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title" id="cancelSubscriptionModalLabel">Cancel Subscription</h5>
                <button type="button" class="btn-close" data-type="cancel-modal-close" aria-label="Close"></button>
            </div>
            <div class="modal-body">
                <div class="text-center py-3">
                    <div class="rounded-circle bg-danger-subtle d-flex align-items-center justify-content-center mx-auto mb-3" style="width: 64px; height: 64px;">
                        <iconify-icon icon="solar:sad-circle-line-duotone" class="fs-9 text-danger"></iconify-icon>
                    </div>
                    <h5 class="mb-2">Are you sure?</h5>
                    <p class="text-muted mb-0">Your subscription will remain active until the end of the current billing period. After that, your plan will revert to the Free tier.</p>
                </div>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-type="cancel-modal-cancel">Keep Subscription</button>
                <button type="button" class="btn btn-danger" data-type="cancel-modal-confirm">Cancel Subscription</button>
            </div>
        </div>
    </div>
</div>
